import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { applyEnrollmentVoucher } from "@/modules/eventRegistration/features/EventWaitlist/api/applyEnrollmentVoucher";
import { fetchPromotionCoveragePreview } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchPromotionCoveragePreview";
import type { PromotionCoverageVoucherOption } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchPromotionCoveragePreview";

interface ApplyVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  enrollmentId: number | null;
  attendeeName: string;
  onApplied: () => void;
}

/**
 * Admin-side voucher application for an unpaid cash seat on the
 * billing tab. Lists the company's usable vouchers (earliest-expiring
 * first — the same order checkout drains them) and applies the chosen
 * one, zeroing the seat and cancelling its payment link.
 */
function ApplyVoucherModal({
  isOpen,
  onClose,
  enrollmentId,
  attendeeName,
  onApplied,
}: ApplyVoucherModalProps) {
  const { toast } = useToast();
  const [isWorking, setIsWorking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVoucherEligible, setIsVoucherEligible] = useState(true);
  const [vouchers, setVouchers] = useState<PromotionCoverageVoucherOption[]>(
    [],
  );
  const [selectedVoucherId, setSelectedVoucherId] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (!isOpen || enrollmentId === null) {
      setVouchers([]);
      setSelectedVoucherId(null);
      return;
    }
    setIsLoading(true);
    fetchPromotionCoveragePreview({ enrollmentId })
      .then((response) => {
        setIsVoucherEligible(response.data.isVoucherEligible);
        setVouchers(response.data.vouchers);
        setSelectedVoucherId(response.data.vouchers[0]?.id ?? null);
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, enrollmentId]);

  const handleApply = async () => {
    if (enrollmentId === null || selectedVoucherId === null) return;
    setIsWorking(true);
    try {
      await applyEnrollmentVoucher({
        enrollmentId,
        voucherId: selectedVoucherId,
      });
      toast({
        title: "Voucher applied",
        description: `${attendeeName}'s seat is now covered by a voucher.`,
      });
      onApplied();
      onClose();
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Apply Voucher</DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-400">
            Cover <strong>{attendeeName}</strong>&apos;s seat with one of the
            company&apos;s vouchers. Any pending payment link for this seat will
            be cancelled.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading vouchers…
          </p>
        )}

        {!isLoading && !isVoucherEligible && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This event is not voucher eligible.
          </p>
        )}

        {!isLoading && isVoucherEligible && vouchers.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This company has no vouchers with seats remaining.
          </p>
        )}

        {!isLoading && vouchers.length > 0 && (
          <select
            className="w-full rounded-md border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-slate-100"
            onChange={(e) => setSelectedVoucherId(Number(e.target.value))}
            value={selectedVoucherId ?? ""}
          >
            {vouchers.map((voucher) => (
              <option key={voucher.id} value={voucher.id}>
                {voucher.name} — {voucher.remainingSeats}{" "}
                {voucher.remainingSeats === 1 ? "seat" : "seats"} left
                {voucher.endDate ? `, expires ${voucher.endDate}` : ""}
              </option>
            ))}
          </select>
        )}

        <DialogFooter>
          <Button disabled={isWorking} onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            disabled={isWorking || isLoading || selectedVoucherId === null}
            onClick={handleApply}
          >
            {isWorking ? "Applying..." : "Apply voucher"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ApplyVoucherModal;
